import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Calendar, Clock, MapPin, Users, UserPlus, Eye, Send, Loader2, Mail, CheckCircle2, XCircle, HelpCircle } from "lucide-react";

const AGE_GROUPS = ["U8", "U9", "U10", "U11", "U12", "U13", "U14", "U15", "U16", "U17", "U18", "U19"];

const InviteStatusBadge = ({ status, attendance }) => {
  if (attendance === "attended") return <Badge className="bg-green-600 text-white text-[10px]">Attended</Badge>;
  if (attendance === "no_show") return <Badge className="bg-red-500 text-white text-[10px]">No Show</Badge>;
  if (status === "accepted") return <Badge className="bg-green-100 text-green-800 border-green-200 text-[10px]">Accepted</Badge>;
  if (status === "declined") return <Badge className="bg-red-100 text-red-800 border-red-200 text-[10px]">Declined</Badge>;
  if (status === "invited") return <Badge className="bg-blue-100 text-blue-800 border-blue-200 text-[10px]">Invited</Badge>;
  return null;
};

const TryoutManager = () => {
  const [tryouts, setTryouts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isOfferOpen, setIsOfferOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewingTryout, setViewingTryout] = useState(null);
  const [offerLead, setOfferLead] = useState(null);
  const [assignTryoutId, setAssignTryoutId] = useState(null);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [sendingOffer, setSendingOffer] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [inviteTryoutId, setInviteTryoutId] = useState(null);
  const [inviteSelectedLeads, setInviteSelectedLeads] = useState([]);
  const [inviteMsg, setInviteMsg] = useState("");
  const [sendingInvites, setSendingInvites] = useState(false);
  const [invites, setInvites] = useState([]);
  const [formData, setFormData] = useState({
    title: "", date: "", time: "", location: "", age_groups: [], description: "", max_spots: null, is_active: true,
    self_schedule: false, slots: [],
  });
  const [offerData, setOfferData] = useState({
    team_name: "", season: "", coach_name: "", practice_schedule: "", custom_message: "", email_subject: ""
  });

  const fetchTryouts = async () => {
    try {
      const res = await axios.get(`${API}/tryouts`, { withCredentials: true });
      setTryouts(res.data);
    } catch (e) { console.error("Failed to fetch tryouts:", e); }
  };

  const fetchLeads = async () => {
    try {
      const res = await axios.get(`${API}/leads`, { withCredentials: true });
      setLeads(res.data);
    } catch (e) { console.error("Failed to fetch leads:", e); }
  };

  useEffect(() => { fetchTryouts(); fetchLeads(); }, []);

  const openDialog = (tryout = null) => {
    if (tryout) {
      setEditing(tryout);
      setFormData({
        title: tryout.title, date: tryout.date, time: tryout.time, location: tryout.location,
        age_groups: tryout.age_groups || [], description: tryout.description || "",
        max_spots: tryout.max_spots || null, is_active: tryout.is_active,
        self_schedule: tryout.self_schedule || false, slots: tryout.slots || [],
      });
    } else {
      setEditing(null);
      setFormData({ title: "", date: "", time: "", location: "", age_groups: [], description: "", max_spots: null, is_active: true, self_schedule: false, slots: [] });
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.date || !formData.time || !formData.location) {
      toast.error("Title, date, time, and location are required"); return;
    }
    try {
      const payload = { ...formData, max_spots: formData.max_spots ? parseInt(formData.max_spots) : null };
      if (editing) {
        await axios.put(`${API}/tryouts/${editing.tryout_id}`, payload, { withCredentials: true });
        toast.success("Tryout updated");
      } else {
        await axios.post(`${API}/tryouts`, payload, { withCredentials: true });
        toast.success("Tryout created");
      }
      setIsDialogOpen(false);
      fetchTryouts();
    } catch (e) { toast.error("Failed to save tryout"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this tryout? Assigned leads will be unassigned.")) return;
    try {
      await axios.delete(`${API}/tryouts/${id}`, { withCredentials: true });
      toast.success("Tryout deleted"); fetchTryouts(); fetchLeads();
    } catch (e) { toast.error("Failed to delete"); }
  };

  const openAssign = (tryoutId) => {
    setAssignTryoutId(tryoutId);
    setSelectedLeads([]);
    setIsAssignOpen(true);
  };

  const handleAssign = async () => {
    if (selectedLeads.length === 0) { toast.error("Select at least one lead"); return; }
    try {
      await axios.post(`${API}/tryouts/assign`, { lead_ids: selectedLeads, tryout_id: assignTryoutId }, { withCredentials: true });
      toast.success(`${selectedLeads.length} lead(s) assigned`);
      setIsAssignOpen(false);
      fetchTryouts(); fetchLeads();
    } catch (e) { toast.error("Failed to assign"); }
  };

  const handleUnassign = async (leadId) => {
    try {
      await axios.post(`${API}/tryouts/unassign/${leadId}`, {}, { withCredentials: true });
      toast.success("Lead unassigned");
      if (viewingTryout) {
        const res = await axios.get(`${API}/tryouts/${viewingTryout.tryout_id}`, { withCredentials: true });
        setViewingTryout(res.data);
      }
      fetchTryouts(); fetchLeads();
    } catch (e) { toast.error("Failed to unassign"); }
  };

  const viewDetail = async (tryout) => {
    try {
      const res = await axios.get(`${API}/tryouts/${tryout.tryout_id}`, { withCredentials: true });
      setViewingTryout(res.data);
      setIsDetailOpen(true);
      fetchInvites(tryout.tryout_id);
    } catch (e) { toast.error("Failed to load details"); }
  };

  const openOffer = (lead) => {
    setOfferLead(lead);
    setOfferData({ team_name: "", season: "", coach_name: "", practice_schedule: "", custom_message: "", email_subject: "" });
    setIsOfferOpen(true);
  };

  const handleSendOffer = async () => {
    if (!offerData.team_name || !offerData.season) { toast.error("Team name and season are required"); return; }
    setSendingOffer(true);
    try {
      await axios.post(`${API}/offers/send`, { lead_id: offerLead.lead_id, ...offerData }, { withCredentials: true });
      toast.success(`Roster offer sent to ${offerLead.name}`);
      setIsOfferOpen(false);
      fetchLeads();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send offer"); }
    finally { setSendingOffer(false); }
  };

  const toggleAgeGroup = (ag) => {
    setFormData((prev) => ({
      ...prev,
      age_groups: prev.age_groups.includes(ag) ? prev.age_groups.filter((a) => a !== ag) : [...prev.age_groups, ag]
    }));
  };

  const toggleLeadSelection = (leadId) => {
    setSelectedLeads((prev) => prev.includes(leadId) ? prev.filter((id) => id !== leadId) : [...prev, leadId]);
  };

  const openBulkInvite = (tryoutId) => {
    setInviteTryoutId(tryoutId);
    setInviteSelectedLeads([]);
    setInviteMsg("");
    setIsInviteOpen(true);
  };

  const toggleInviteLeadSelection = (leadId) => {
    setInviteSelectedLeads((prev) => prev.includes(leadId) ? prev.filter((id) => id !== leadId) : [...prev, leadId]);
  };

  const handleBulkInvite = async () => {
    if (inviteSelectedLeads.length === 0) return;
    setSendingInvites(true);
    try {
      await axios.post(`${API}/tryouts/invite`, {
        lead_ids: inviteSelectedLeads,
        tryout_id: inviteTryoutId,
        custom_message: inviteMsg || null,
      }, { withCredentials: true });
      toast.success(`Invited ${inviteSelectedLeads.length} leads`);
      setIsInviteOpen(false);
      fetchTryouts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send invites");
    } finally {
      setSendingInvites(false);
    }
  };

  const fetchInvites = async (tryoutId) => {
    try {
      const res = await axios.get(`${API}/tryouts/${tryoutId}/invites`, { withCredentials: true });
      setInvites(res.data || []);
    } catch {
      setInvites([]);
    }
  };

  const markAttendance = async (inviteId, attendance) => {
    try {
      await axios.put(`${API}/tryouts/invites/${inviteId}/attendance`, { attendance }, { withCredentials: true });
      toast.success("Attendance updated");
      if (viewingTryout) fetchInvites(viewingTryout.tryout_id);
    } catch {
      toast.error("Failed to update attendance");
    }
  };

  const unassignedLeads = leads.filter((l) => !l.tryout_id);

  return (
    <div data-testid="tryout-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-cfc-primary">Tryout Schedule</h1>
          <p className="text-cfc-text-muted text-sm mt-1">Create tryout events and assign leads from the join form</p>
        </div>
        <Button onClick={() => openDialog()} className="bg-cfc-accent hover:bg-cfc-accent-hover text-cfc-primary" data-testid="add-tryout-btn">
          <Plus size={18} className="mr-2" /> New Tryout
        </Button>
      </div>

      <div className="space-y-4">
        {tryouts.map((tryout) => (
          <Card key={tryout.tryout_id} className="border-cfc-border" data-testid={`tryout-${tryout.tryout_id}`}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CardTitle className="font-heading text-xl text-cfc-primary">{tryout.title}</CardTitle>
                  <Badge variant={tryout.is_active ? "default" : "secondary"}>{tryout.is_active ? "Active" : "Inactive"}</Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => viewDetail(tryout)} data-testid={`view-tryout-${tryout.tryout_id}`}>
                    <Eye size={14} className="mr-1" /> View
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => openAssign(tryout.tryout_id)} data-testid={`assign-tryout-${tryout.tryout_id}`}>
                    <UserPlus size={14} className="mr-1" /> Assign
                  </Button>
                  <Button variant="outline" size="sm" className="text-blue-600 border-blue-200" onClick={() => openBulkInvite(tryout.tryout_id)} data-testid={`invite-tryout-${tryout.tryout_id}`}>
                    <Mail size={14} className="mr-1" /> Invite
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => openDialog(tryout)}><Edit size={14} /></Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(tryout.tryout_id)} className="text-cfc-destructive"><Trash2 size={14} /></Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4 text-sm text-cfc-text-muted">
                <span className="flex items-center gap-1"><Calendar size={14} /> {tryout.date}</span>
                <span className="flex items-center gap-1"><Clock size={14} /> {tryout.time}</span>
                <span className="flex items-center gap-1"><MapPin size={14} /> {tryout.location}</span>
                <span className="flex items-center gap-1"><Users size={14} /> {tryout.assigned_count || 0} assigned{tryout.max_spots ? ` / ${tryout.max_spots} spots` : ""}</span>
              </div>
              {tryout.age_groups?.length > 0 && (
                <div className="flex gap-1.5 mt-2">
                  {tryout.age_groups.map((ag) => (
                    <Badge key={ag} variant="outline" className="text-xs">{ag}</Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
        {tryouts.length === 0 && (
          <div className="text-center py-12 text-cfc-text-muted">
            <Calendar size={32} className="mx-auto mb-3 opacity-50" />
            <p>No tryouts scheduled yet. Create one to start assigning leads.</p>
          </div>
        )}
      </div>

      {/* Create/Edit Tryout Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="tryout-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-cfc-primary">{editing ? "Edit Tryout" : "New Tryout"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div><Label>Title</Label><Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="Spring Open Tryouts" data-testid="tryout-title-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Date</Label><Input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} data-testid="tryout-date-input" /></div>
              <div><Label>Time</Label><Input value={formData.time} onChange={(e) => setFormData({ ...formData, time: e.target.value })} placeholder="9:00 AM - 12:00 PM" data-testid="tryout-time-input" /></div>
            </div>
            <div><Label>Location</Label><Input value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} placeholder="Main Soccer Complex, Field 3" data-testid="tryout-location-input" /></div>
            <div><Label>Description (optional)</Label><Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={2} placeholder="What to bring, what to expect..." /></div>
            <div><Label>Max Spots (optional)</Label><Input type="number" value={formData.max_spots || ""} onChange={(e) => setFormData({ ...formData, max_spots: e.target.value })} placeholder="Leave empty for unlimited" /></div>
            <div>
              <Label className="mb-2 block">Age Groups</Label>
              <div className="flex flex-wrap gap-2">
                {AGE_GROUPS.map((ag) => (
                  <button key={ag} type="button" onClick={() => toggleAgeGroup(ag)}
                    className={`text-xs px-3 py-1.5 rounded border transition-colors ${formData.age_groups.includes(ag) ? "bg-cfc-accent text-cfc-primary border-cfc-accent font-bold" : "border-cfc-border text-cfc-text-muted hover:border-cfc-primary"}`}
                    data-testid={`age-group-${ag}`}
                  >{ag}</button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={formData.is_active} onCheckedChange={(c) => setFormData({ ...formData, is_active: c })} />
              <Label>Active (visible to public)</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={formData.self_schedule} onCheckedChange={(c) => setFormData({ ...formData, self_schedule: c })} data-testid="self-schedule-toggle" />
              <Label>Enable Self-Scheduling (parents pick their own slot)</Label>
            </div>
            {formData.self_schedule && (
              <div className="border border-cfc-border rounded p-3 space-y-3" data-testid="slot-builder">
                <Label className="text-cfc-primary font-medium">Time Slots</Label>
                <p className="text-xs text-cfc-text-muted">Add time slots that parents can choose from. Leave max spots empty for unlimited.</p>
                {formData.slots.map((slot, idx) => (
                  <div key={slot.slot_id || idx} className="flex items-center gap-2">
                    <Input value={slot.time || ""} onChange={(e) => { const s = [...formData.slots]; s[idx] = { ...s[idx], time: e.target.value }; setFormData({ ...formData, slots: s }); }} placeholder="Start (e.g. 6:00 PM)" className="w-28 text-xs" />
                    <span className="text-xs text-cfc-text-muted">to</span>
                    <Input value={slot.end_time || ""} onChange={(e) => { const s = [...formData.slots]; s[idx] = { ...s[idx], end_time: e.target.value }; setFormData({ ...formData, slots: s }); }} placeholder="End (e.g. 7:00 PM)" className="w-28 text-xs" />
                    <Input value={slot.age_group || ""} onChange={(e) => { const s = [...formData.slots]; s[idx] = { ...s[idx], age_group: e.target.value }; setFormData({ ...formData, slots: s }); }} placeholder="Age group" className="w-24 text-xs" />
                    <Input type="number" value={slot.max_spots || ""} onChange={(e) => { const s = [...formData.slots]; s[idx] = { ...s[idx], max_spots: e.target.value ? parseInt(e.target.value) : null }; setFormData({ ...formData, slots: s }); }} placeholder="Max" className="w-16 text-xs" />
                    <Button variant="ghost" size="sm" className="text-red-500 h-7 w-7 p-0" onClick={() => { const s = [...formData.slots]; s.splice(idx, 1); setFormData({ ...formData, slots: s }); }}>x</Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => setFormData({ ...formData, slots: [...formData.slots, { slot_id: `slot_${Date.now()}`, time: "", end_time: "", age_group: "", max_spots: null }] })} data-testid="add-slot-btn">
                  + Add Slot
                </Button>
              </div>
            )}
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={handleSubmit} className="bg-cfc-primary hover:bg-cfc-primary-hover" data-testid="save-tryout-btn">{editing ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Leads Dialog */}
      <Dialog open={isAssignOpen} onOpenChange={setIsAssignOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="assign-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-cfc-primary">Assign Leads to Tryout</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-cfc-text-muted mb-3">{unassignedLeads.length} unassigned lead(s) available</p>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {unassignedLeads.map((lead) => (
              <label key={lead.lead_id} className={`flex items-center gap-3 p-3 rounded border cursor-pointer transition-colors ${selectedLeads.includes(lead.lead_id) ? "border-cfc-accent bg-cfc-accent/10" : "border-cfc-border hover:bg-gray-50"}`}>
                <input type="checkbox" checked={selectedLeads.includes(lead.lead_id)} onChange={() => toggleLeadSelection(lead.lead_id)} className="rounded" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-cfc-primary">{lead.name}</p>
                  <p className="text-xs text-cfc-text-muted">{lead.email} | {lead.player_age}</p>
                </div>
              </label>
            ))}
            {unassignedLeads.length === 0 && <p className="text-sm text-cfc-text-muted py-4 text-center">All leads are already assigned to tryouts.</p>}
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={handleAssign} disabled={selectedLeads.length === 0} className="bg-cfc-primary hover:bg-cfc-primary-hover" data-testid="confirm-assign-btn">
              Assign {selectedLeads.length} Lead(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tryout Detail / Assigned Leads Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="tryout-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-cfc-primary">{viewingTryout?.title}</DialogTitle>
          </DialogHeader>
          {viewingTryout && (
            <div>
              <div className="flex flex-wrap gap-4 text-sm text-cfc-text-muted mb-6">
                <span className="flex items-center gap-1"><Calendar size={14} /> {viewingTryout.date}</span>
                <span className="flex items-center gap-1"><Clock size={14} /> {viewingTryout.time}</span>
                <span className="flex items-center gap-1"><MapPin size={14} /> {viewingTryout.location}</span>
              </div>

              {/* Invite Summary */}
              {invites.length > 0 && (
                <div className="flex gap-3 mb-4 text-xs">
                  <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded font-medium">{invites.length} Invited</span>
                  <span className="px-2 py-1 bg-green-50 text-green-700 rounded font-medium">{invites.filter(i => i.status === "accepted").length} Accepted</span>
                  <span className="px-2 py-1 bg-red-50 text-red-700 rounded font-medium">{invites.filter(i => i.status === "declined").length} Declined</span>
                  <span className="px-2 py-1 bg-gray-50 text-gray-600 rounded font-medium">{invites.filter(i => i.status === "invited").length} Pending</span>
                  <span className="px-2 py-1 bg-emerald-50 text-emerald-700 rounded font-medium">{invites.filter(i => i.attendance === "attended").length} Attended</span>
                </div>
              )}

              {/* Invites List */}
              {invites.length > 0 && (
                <>
                  <h3 className="font-heading text-lg text-cfc-primary mb-3">Invitations ({invites.length})</h3>
                  <div className="space-y-2 mb-6">
                    {invites.map((inv) => (
                      <div key={inv.invite_id} className="flex items-center justify-between p-3 border border-cfc-border rounded" data-testid={`invite-${inv.invite_id}`}>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-cfc-primary">{inv.lead_name}</p>
                          <p className="text-xs text-cfc-text-muted">{inv.lead_email}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <InviteStatusBadge status={inv.status} attendance={inv.attendance} />
                            {inv.responded_at && (
                              <span className="text-[10px] text-cfc-text-muted">
                                {new Date(inv.responded_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <Button
                            variant={inv.attendance === "attended" ? "default" : "outline"}
                            size="sm"
                            className={`text-[10px] h-7 ${inv.attendance === "attended" ? "bg-green-600" : ""}`}
                            onClick={() => markAttendance(inv.invite_id, inv.attendance === "attended" ? null : "attended")}
                            data-testid={`mark-attended-${inv.invite_id}`}
                          >
                            <CheckCircle2 size={12} className="mr-0.5" /> Attended
                          </Button>
                          <Button
                            variant={inv.attendance === "no_show" ? "default" : "outline"}
                            size="sm"
                            className={`text-[10px] h-7 ${inv.attendance === "no_show" ? "bg-red-500" : ""}`}
                            onClick={() => markAttendance(inv.invite_id, inv.attendance === "no_show" ? null : "no_show")}
                            data-testid={`mark-noshow-${inv.invite_id}`}
                          >
                            <XCircle size={12} className="mr-0.5" /> No Show
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              <h3 className="font-heading text-lg text-cfc-primary mb-3">
                Assigned Players ({viewingTryout.assigned_leads?.length || 0})
              </h3>
              <div className="space-y-2">
                {(viewingTryout.assigned_leads || []).map((lead) => (
                  <div key={lead.lead_id} className="flex items-center justify-between p-3 border border-cfc-border rounded">
                    <div>
                      <p className="text-sm font-medium text-cfc-primary">{lead.name}</p>
                      <p className="text-xs text-cfc-text-muted">{lead.email} | {lead.player_age}</p>
                      {lead.offer_status && (
                        <Badge variant={lead.offer_status === "accepted" ? "default" : lead.offer_status === "declined" ? "destructive" : "secondary"} className="mt-1 text-xs">
                          Offer: {lead.offer_status}
                        </Badge>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {!lead.offer_status && (
                        <Button variant="outline" size="sm" onClick={() => openOffer(lead)} data-testid={`send-offer-${lead.lead_id}`}>
                          <Send size={14} className="mr-1" /> Send Offer
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => handleUnassign(lead.lead_id)} className="text-cfc-destructive text-xs">
                        Unassign
                      </Button>
                    </div>
                  </div>
                ))}
                {(!viewingTryout.assigned_leads || viewingTryout.assigned_leads.length === 0) && (
                  <p className="text-sm text-cfc-text-muted py-4 text-center">No players assigned yet.</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Invite Dialog */}
      <Dialog open={isInviteOpen} onOpenChange={setIsInviteOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-cfc-primary">Invite Leads to Tryout</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="mb-2 block text-xs text-cfc-text-muted">Select leads to invite (showing unassigned leads with emails):</Label>
              <div className="border border-cfc-border rounded max-h-48 overflow-y-auto">
                {leads.filter(l => l.email).map((lead) => (
                  <label key={lead.lead_id} className="flex items-center gap-3 px-3 py-2 hover:bg-cfc-cream cursor-pointer border-b border-cfc-border/50 last:border-0">
                    <input
                      type="checkbox"
                      checked={inviteSelectedLeads.includes(lead.lead_id)}
                      onChange={() => toggleInviteLeadSelection(lead.lead_id)}
                      className="rounded"
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-cfc-primary truncate">{lead.name}</p>
                      <p className="text-[11px] text-cfc-text-muted truncate">{lead.email} • {lead.player_age}</p>
                    </div>
                  </label>
                ))}
              </div>
              <p className="text-[11px] text-cfc-text-muted mt-1">{inviteSelectedLeads.length} selected</p>
            </div>
            <div>
              <Label>Custom Message (optional)</Label>
              <Textarea
                value={inviteMsg}
                onChange={(e) => setInviteMsg(e.target.value)}
                placeholder="Add a personal note to the invite email..."
                rows={2}
                className="text-sm"
              />
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button
              onClick={handleBulkInvite}
              disabled={sendingInvites || inviteSelectedLeads.length === 0}
              className="bg-cfc-primary hover:bg-cfc-primary-hover"
              data-testid="confirm-invite-btn"
            >
              {sendingInvites ? <><Loader2 size={16} className="mr-2 animate-spin" /> Sending...</> : <><Mail size={16} className="mr-2" /> Invite {inviteSelectedLeads.length} Lead(s)</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Roster Offer Dialog */}
      <Dialog open={isOfferOpen} onOpenChange={setIsOfferOpen}>
        <DialogContent className="max-w-lg" data-testid="offer-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-cfc-primary">Send Roster Offer</DialogTitle>
          </DialogHeader>
          {offerLead && (
            <div className="space-y-4 py-4">
              <div className="bg-cfc-cream-alt p-3 rounded">
                <p className="text-sm font-medium text-cfc-primary">{offerLead.name}</p>
                <p className="text-xs text-cfc-text-muted">{offerLead.email} | {offerLead.player_age}</p>
              </div>
              <div><Label>Team Name *</Label><Input value={offerData.team_name} onChange={(e) => setOfferData({ ...offerData, team_name: e.target.value })} placeholder="e.g. U14 Elite" data-testid="offer-team-input" /></div>
              <div><Label>Season *</Label><Input value={offerData.season} onChange={(e) => setOfferData({ ...offerData, season: e.target.value })} placeholder="e.g. Fall 2026" data-testid="offer-season-input" /></div>
              <div><Label>Coach Name (optional)</Label><Input value={offerData.coach_name} onChange={(e) => setOfferData({ ...offerData, coach_name: e.target.value })} placeholder="Coach Smith" /></div>
              <div><Label>Practice Schedule (optional)</Label><Input value={offerData.practice_schedule} onChange={(e) => setOfferData({ ...offerData, practice_schedule: e.target.value })} placeholder="Tues/Thurs 6-8 PM" /></div>
              <div><Label>Custom Message (optional)</Label><Textarea value={offerData.custom_message} onChange={(e) => setOfferData({ ...offerData, custom_message: e.target.value })} rows={3} placeholder="Any additional notes for the player/family..." /></div>
              <div><Label>Email Subject (optional, uses template default)</Label><Input value={offerData.email_subject} onChange={(e) => setOfferData({ ...offerData, email_subject: e.target.value })} placeholder="Leave blank for default" /></div>
            </div>
          )}
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={handleSendOffer} disabled={sendingOffer} className="bg-cfc-primary hover:bg-cfc-primary-hover" data-testid="confirm-offer-btn">
              {sendingOffer ? <><Loader2 size={16} className="mr-2 animate-spin" /> Sending...</> : <><Send size={16} className="mr-2" /> Send Offer</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TryoutManager;
